<template>
  <div class="manufacturer-firmware">
    <!-- Géré par le constructeur : ses firmwares sont les versions annoncées, il installe toujours sa dernière -->
    <v-alert v-if="sheet.managedUpdates" type="info" text dense class="mb-4">{{ $t('manufacturer.sheet.managed-alert', {location: sheet.managedUpdateLocation}) }}</v-alert>

    <!-- Où en sont ses bornes : chaque chiffre lance la mise à jour des bornes qu'il compte -->
    <v-row dense class="mb-2">
      <v-col cols="6" md="3">
        <v-card outlined class="fill-height"><v-card-text>
          <div class="text-caption">{{ $t('manufacturer.sheet.charging-points') }}</div>
          <div class="text-h5 text--primary">{{ sheet.total }}</div>
          <div class="text-caption">{{ $tc('manufacturer.sheet.models-count', sheet.models.length, {count: sheet.models.length}) }}</div>
          <v-btn v-if="sheet.total" x-small text color="primary" class="px-1" :to="{name: 'charging-points', query: {manufacturerId: id}}">{{ $t('product-line.see-charging-points') }}</v-btn>
        </v-card-text></v-card>
      </v-col>
      <v-col cols="6" md="3">
        <v-card outlined class="fill-height"><v-card-text>
          <div class="text-caption">{{ $t('firmware.advice.up-to-date') }}</div>
          <div class="text-h5" :class="sheet.counters['up-to-date'] ? 'success--text' : 'text--secondary'">{{ sheet.counters['up-to-date'] }}</div>
          <div class="text-caption">{{ percent(sheet.counters['up-to-date']) }}</div>
        </v-card-text></v-card>
      </v-col>
      <v-col cols="6" md="3">
        <v-card outlined class="fill-height" :class="{'card--focus': sheet.counters['update-available']}"><v-card-text>
          <div class="text-caption">{{ $t('firmware.advice.update-available') }}</div>
          <div class="text-h5" :class="sheet.counters['update-available'] ? 'primary--text' : 'text--secondary'">{{ sheet.counters['update-available'] }}</div>
          <v-btn v-if="canDeployFirmware && sheet.counters['update-available']" x-small color="primary" class="mt-1" :loading="launching === 'all'" @click="launch({state: 'update-available'}, 'all')">
            {{ $t('manufacturer.sheet.update') }}
          </v-btn>
          <v-btn v-if="canDeployFirmware && sheet.total" x-small text color="primary" class="mt-1 px-1" @click="openPerimeter">{{ $t('manufacturer.sheet.perimeter') }}</v-btn>
        </v-card-text></v-card>
      </v-col>
      <v-col cols="6" md="3">
        <v-card outlined class="fill-height"><v-card-text>
          <div class="text-caption">{{ $t('firmware.advice.no-path') }}</div>
          <div class="text-h5" :class="sheet.counters['no-path'] ? 'orange--text' : 'text--secondary'">{{ sheet.counters['no-path'] }}</div>
          <div class="text-caption">{{ $t('manufacturer.sheet.not-covered', {count: sheet.counters['not-covered']}) }}</div>
          <div v-if="sheet.counters.pinned" class="text-caption deep-purple--text">{{ $t('manufacturer.sheet.pinned', {count: sheet.counters.pinned}) }}</div>
        </v-card-text></v-card>
      </v-col>
    </v-row>

    <!-- Firmwares -->
    <v-card outlined class="mb-4">
      <v-card-title class="text-subtitle-1">
        {{ $t('manufacturer.sheet.firmwares') }}
        <v-spacer />
        <v-btn v-if="canEditCatalogue && !sheet.managedUpdates" small color="primary" :to="{name: 'firmware.new', params: {manufacturerId: id}}">
          <v-icon small left>mdi-plus</v-icon>{{ $t('manufacturer.sheet.new-firmware') }}
        </v-btn>
      </v-card-title>
      <v-data-table :headers="firmwareHeaders" :items="sheet.firmwares" :items-per-page="-1" hide-default-footer :no-data-text="$t('manufacturer.sheet.no-firmware')" item-key="id">
        <template #item.name="{item}">
          <router-link :to="{name: 'firmware.edit', params: {id: item.id}}" class="mono font-weight-medium">{{ item.name }}</router-link>
          <v-chip v-if="!item.version" x-small outlined color="orange" class="ml-1">{{ $t('manufacturer.sheet.version-pending') }}</v-chip>
          <v-chip v-if="item.managed" x-small outlined class="ml-1">{{ $t('manufacturer.sheet.managed-chip') }}</v-chip>
        </template>
        <template #item.models="{item}">
          <span v-if="!item.models.length" class="text--secondary">{{ $t('manufacturer.sheet.all-models') }}</span>
          <span v-else class="mono">{{ item.models.join(', ') }}</span>
        </template>
        <template #item.installableFrom="{item}">
          <span v-if="!item.installableFrom.length" class="text--secondary">{{ $t('manufacturer.sheet.from-any') }}</span>
          <span v-else class="mono">{{ item.installableFrom.join(', ') }}</span>
        </template>
        <template #item.status="{item}">
          <v-chip x-small :color="firmwareStatusColor(item.status)" text-color="white">{{ $t('firmware.firmware-statuses.' + item.status) }}</v-chip>
          <div v-if="item.status === 'testing' && item.trial" class="text-caption">{{ $t('firmware.trial.proposals-short.' + item.trial.proposal) }}</div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Versions annoncées sans firmware : une ligne chacune, alignée ; quelques modèles, la liste entière à la demande -->
    <v-card v-if="sheet.observed.length && !sheet.managedUpdates" outlined class="mb-4">
      <v-card-title class="text-subtitle-1">{{ $t('manufacturer.sheet.observed') }}</v-card-title>
      <v-card-subtitle>{{ $t('manufacturer.sheet.observed-hint') }}</v-card-subtitle>
      <v-card-text class="pb-2">
        <div v-for="observed in shownObserved" :key="observed.version" class="observed-row">
          <span class="mono font-weight-medium">{{ observed.version }}</span>
          <span class="observed-cell">
            <span class="observed-models text-caption text--secondary">
              <span class="text-truncate">{{ observed.models.slice(0, MODELS_SHOWN).join(', ') }}</span>
              <a v-if="observed.models.length > MODELS_SHOWN" class="text-no-wrap ml-1" @click="modelsOf = observed">{{ $t('manufacturer.sheet.more-models', {count: observed.models.length - MODELS_SHOWN}) }}</a>
            </span>
            <!-- Sans firmware à son nom, mais ses bornes peuvent partir vers ceux-ci -->
            <span v-if="observed.installableTo && observed.installableTo.length" class="text-caption success--text d-block text-truncate">{{ $t('manufacturer.sheet.observed-path', {versions: observed.installableTo.join(', ')}) }}</span>
          </span>
          <span class="text-body-2 text-no-wrap text-right">{{ $tc('manufacturer.list.charging-points', observed.count, {count: observed.count}) }}</span>
          <span class="text-right">
            <v-btn v-if="canEditCatalogue" x-small outlined :to="createFrom(observed)">{{ $t('manufacturer.sheet.create-firmware') }}</v-btn>
          </span>
        </div>
        <div v-if="sheet.observed.length > VERSIONS_SHOWN" class="mt-2">
          <a class="text-body-2" @click="allObserved = !allObserved">
            {{ allObserved ? $t('manufacturer.sheet.fewer-versions') : $t('manufacturer.sheet.more-versions', {count: sheet.observed.length - VERSIONS_SHOWN}) }}
          </a>
        </div>
      </v-card-text>
    </v-card>

    <!-- Ses modèles annoncés : les versions qu'ils ont, où ils en sont, comment on leur transfère un fichier -->
    <v-card outlined class="mb-4">
      <v-card-title class="text-subtitle-1">{{ $t('manufacturer.sheet.models') }}</v-card-title>
      <v-data-table :headers="modelHeaders" :items="sheet.models" :items-per-page="-1" hide-default-footer item-key="model">
        <template #item.model="{item}"><span class="mono text-no-wrap">{{ item.model || '-' }}</span></template>
        <template #item.versions="{item}">
          <span v-for="version in item.versions.slice(0, 3)" :key="version.version" class="text-caption mr-2"><span class="mono">{{ version.version }}</span> ({{ version.count }})</span>
          <span v-if="item.versions.length > 3" class="text-caption text--secondary">+{{ item.versions.length - 3 }}</span>
        </template>
        <template #item.upToDate="{item}"><span :class="item.counters['up-to-date'] ? 'success--text' : 'text--secondary'">{{ item.counters['up-to-date'] }}</span></template>
        <template #item.available="{item}">
          <a v-if="canDeployFirmware && item.counters['update-available']" @click="launch({state: 'update-available', model: item.model}, item.model)">{{ item.counters['update-available'] }}</a>
          <span v-else class="text--secondary">{{ item.counters['update-available'] }}</span>
        </template>
        <template #item.noPath="{item}">
          <span v-if="item.counters['not-covered'] === item.count" class="text--secondary">{{ $t('manufacturer.sheet.no-firmware-for-model') }}</span>
          <span v-else :class="item.counters['no-path'] ? 'orange--text' : 'text--secondary'">{{ item.counters['no-path'] }}</span>
        </template>
        <template #item.transfer="{item}">
          <span v-if="item.firmwareProtocol === sheet.firmwareProtocol && item.diagnosticProtocol === sheet.diagnosticProtocol" class="text--secondary">{{ $t('manufacturer.sheet.default-transfer') }}</span>
          <span v-else>{{ item.firmwareProtocol.toUpperCase() }} · {{ item.diagnosticProtocol.toUpperCase() }}</span>
        </template>
      </v-data-table>
    </v-card>

    <!-- Mettre à jour un périmètre : une organisation, un modèle, une version annoncée -->
    <v-dialog v-model="perimeter.open" max-width="520">
      <v-card v-if="perimeter.open">
        <v-card-title>{{ $t('manufacturer.sheet.perimeter-title', {name: sheet.name}) }}</v-card-title>
        <v-card-text>
          <organization-select v-model="perimeter.organizationId" :label="$t('manufacturer.sheet.perimeter-organization')" clearable />
          <v-select v-model="perimeter.model" :items="modelNames" :label="$t('manufacturer.sheet.perimeter-model')" clearable outlined dense class="mt-3" />
          <v-combobox v-model="perimeter.version" :items="observedVersions" :label="$t('manufacturer.sheet.perimeter-version')" :hint="$t('manufacturer.sheet.perimeter-version-hint')" persistent-hint clearable outlined dense class="mono" />
          <v-alert type="info" text dense class="mt-4 mb-0">
            <v-progress-circular v-if="perimeter.counting" indeterminate size="16" class="mr-2" />
            {{ $tc('manufacturer.sheet.perimeter-count', perimeter.ids.length, {count: perimeter.ids.length}) }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn small text @click="perimeter.open = false">{{ $t('cancel') }}</v-btn>
          <v-btn small color="primary" :disabled="!perimeter.ids.length" @click="$router.push({name: 'firmware.launch', params: {chargingPointIds: perimeter.ids}})">{{ $t('manufacturer.sheet.perimeter-launch') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Les modèles d'une version, tous, avec leur nombre de bornes -->
    <v-dialog :value="!!modelsOf" max-width="520" scrollable @input="modelsOf = null">
      <v-card v-if="modelsOf">
        <v-card-title class="d-flex align-center">
          <div class="flex-grow-1">
            <div class="text-overline text--secondary" style="line-height: 1.4">{{ $t('manufacturer.sheet.models-of') }}</div>
            <div class="mono text-h6" style="line-height: 1.3">{{ modelsOf.version }}</div>
          </div>
          <v-btn icon small :aria-label="$t('close')" @click="modelsOf = null"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-2">
          <v-simple-table dense>
            <tbody>
              <tr v-for="row in modelsOf.modelCounts" :key="row.model">
                <td class="mono">{{ row.model }}</td>
                <td class="text-right text--secondary">{{ $tc('manufacturer.list.charging-points', row.count, {count: row.count}) }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <span class="text-body-2 text--secondary ml-2">{{ $tc('manufacturer.list.charging-points', modelsOf.count, {count: modelsOf.count}) }}</span>
          <v-spacer />
          <v-btn v-if="canEditCatalogue" small color="primary" depressed :to="createFrom(modelsOf)">{{ $t('manufacturer.sheet.create-firmware') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import FirmwareRepository from "@repository/FirmwareRepository";
import FirmwareMixin from "@pages/Firmware/FirmwareMixin";
import OrganizationSelect from "@blocks/Select/OrganizationSelect.vue";

/**
 * Le firmware d'un constructeur : où en sont ses bornes, ses firmwares, les versions annoncées sans firmware (à créer),
 * ses modèles annoncés et leurs versions ; mettre à jour tout ou partie du parc.
 */
export default {
  components: {OrganizationSelect},
  mixins: [FirmwareMixin],

  data() {
    return {
      launching: null,
      modelsOf: null,
      allObserved: false,
      perimeter: {open: false, organizationId: null, model: null, version: null, ids: [], counting: false},
      MODELS_SHOWN: 3,
      VERSIONS_SHOWN: 10,
    }
  },

  computed: {
    id() {
      return this.$route.params.id
    },
    sheet() {
      return this.$parent.sheet
    },
    // Les plus répandues d'abord (le serveur les trie) : dix, et les autres à la demande.
    shownObserved() {
      return this.allObserved ? this.sheet.observed : this.sheet.observed.slice(0, this.VERSIONS_SHOWN)
    },
    observedVersions() {
      return (this.sheet.observed || []).map(row => row.version)
    },
    modelNames() {
      return this.sheet.models.map(model => model.model).filter(Boolean)
    },
    firmwareHeaders() {
      return [
        {text: this.$t('manufacturer.sheet.columns.version'), value: 'name'},
        {text: this.$t('manufacturer.sheet.columns.models'), value: 'models', sortable: false},
        {text: this.$t('manufacturer.sheet.columns.from'), value: 'installableFrom', sortable: false},
        {text: this.$t('manufacturer.sheet.columns.status'), value: 'status'},
        {text: this.$t('manufacturer.sheet.columns.installed'), value: 'installed', align: 'end'},
      ]
    },
    modelHeaders() {
      return [
        {text: this.$t('manufacturer.sheet.columns.model'), value: 'model'},
        {text: this.$t('manufacturer.sheet.columns.versions'), value: 'versions', sortable: false},
        {text: this.$t('firmware.advice.up-to-date'), value: 'upToDate', align: 'end', sortable: false},
        {text: this.$t('firmware.advice.update-available'), value: 'available', align: 'end', sortable: false},
        {text: this.$t('firmware.advice.no-path'), value: 'noPath', align: 'end', sortable: false},
        {text: this.$t('manufacturer.sheet.columns.transfer'), value: 'transfer', sortable: false},
      ]
    },
  },

  watch: {
    'perimeter.organizationId'() {
      this.countPerimeter()
    },
    'perimeter.model'() {
      this.countPerimeter()
    },
    'perimeter.version'() {
      this.countPerimeter()
    },
  },

  methods: {
    // Créer le firmware d'une version annoncée : la version et tous ses modèles pré-remplis.
    createFrom(observed) {
      return {name: 'firmware.new', params: {manufacturerId: this.id}, query: {version: observed.version, models: observed.models}}
    },

    openPerimeter() {
      this.perimeter = {open: true, organizationId: null, model: null, version: null, ids: [], counting: false}
      this.countPerimeter()
    },

    countPerimeter() {
      if (!this.perimeter.open) {
        return
      }
      const {organizationId, model, version} = this.perimeter
      this.perimeter.counting = true
      FirmwareRepository.selection({manufacturerId: this.id, organizationId, model, version: version || null}).then(({chargingPointIds}) => {
        this.perimeter.ids = chargingPointIds
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.perimeter.counting = false
      })
    },

    // Un chiffre mène à la page de lancement avec les bornes qu'il compte.
    launch(filters, key) {
      this.launching = key
      FirmwareRepository.selection({manufacturerId: this.id, ...filters}).then(({chargingPointIds}) => {
        this.$router.push({name: 'firmware.launch', params: {chargingPointIds}})
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.launching = null
      })
    },

    percent(count) {
      return this.sheet.total ? `${Math.round((count / this.sheet.total) * 100)} %` : '-'
    },
  },
}
</script>

<style scoped lang="scss">
.manufacturer-firmware {
  // Colonnes fixes : la version, les modèles (tronqués, jamais sur deux lignes), le nombre, le bouton.
  .observed-row {
    display: grid;
    grid-template-columns: 150px minmax(0, 1fr) 100px 150px;
    align-items: center;
    gap: 16px;
    padding: 6px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    &:last-of-type {
      border-bottom: none;
    }
    @media (max-width: 700px) {
      grid-template-columns: 1fr auto;
      .observed-models {
        grid-column: 1 / -1;
        order: 3;
      }
    }
  }
  .observed-models {
    display: flex;
    min-width: 0;
  }
  .observed-cell {
    min-width: 0;
  }
  .mono {
    font-family: ui-monospace, monospace;
  }
  .card--focus {
    border: 2px solid var(--v-primary-base) !important;
  }
}
</style>
